<div class="container pqr">
  <h1 class="page-title">Padrão de Qualidade de Referência</h1>

  <hr />

  <h3 class="section-title">Apresentação</h3>

  <div class="apresentacao-text">
    <p class="text">
      O SimCAQ utiliza um conjunto de parâmetros relativos aos insumos e recursos monetários denominados como Padrão de Qualidade de Referência (PQR) para estimar o custo da oferta de ensino em condições de qualidade nas escolas públicas de educação básica.
    </p>

    <p class="text">
      Os parâmetros que compõem o PQR dialogam com a necessidade de operacionalizar o que prevê a Constituição Federal de 1988 (Artigo 211 § 1º) e a Lei Diretrizes e Bases da Educação Nacional (LDB/1996), sobre o dever do Estado referente à garantia de "padrões mínimos de qualidade de ensino", estabelecidos na LDB/1996 "como a variedade e quantidade mínimas, por aluno, de insumos indispensáveis ao desenvolvimento do processo de ensino-aprendizagem [...]" (Brasil, 1996, Art. 4º).
    </p>

    <p class="text">
      O PQR também se articula às dimensões dos padrões mínimos de qualidade da educação básica, referentes às condições de oferta, estabelecidas pela Lei que institui o Sistema Nacional de Educação (SNE) (Brasil, 2025a, art. 34):
    </p>

    <ul class="pqr-list">
      <li>I - jornada escolar mínima nos estabelecimentos de ensino, com progressiva extensão para jornada em tempo integral;</li>
      <li>II - adequada razão professor-aluno por turma;</li>
      <li>III - formação docente adequada às áreas de atuação;</li>
      <li>IV - existência de plano de carreira e de piso salarial profissional nacional dos profissionais do magistério público;</li>
      <li>V - nível de profissionalização e de qualificação dos profissionais da educação não docentes;</li>
      <li>VI - estrutura física e instalações escolares com padrões de conforto ambiental, espaços apropriados para o desenvolvimento integral do processo pedagógico, salubridade, água potável e instalações sanitárias adequadas, acessibilidade e sustentabilidade ambiental;</li>
      <li>VII - recursos educacionais e tecnologias digitais;</li>
      <li>VIII - serviços complementares de apoio ao aluno. (Brasil, 2025a, art. 34).</li>
    </ul>

    <p class="text">
      O PQR, dialoga com a Lei do SNE, sendo composto de parâmetros referentes aos padrões mínimos de qualidade passíveis de monetização e considera as características das etapas e das modalidades de ensino e a variação dos insumos de acordo com o local de cada rede de ensino (Brasil, 2025a, artigo 41, §1º). Cada parâmetro do PQR foi definido a partir de normatizações existentes, por exemplo, leis, decretos e resoluções em âmbito nacional e, na ausência desses, com base em documentos orientadores de políticas públicas, em estudos e pesquisas acadêmicas (Alves, 2012; Alves et al. 2019, Alves, Silveira, Schneider, 2019) e na proposição do CAQ/CAQi da Campanha Nacional pelo Direito à Educação (Campanha Nacional pelo Direito à Educação, 2018). Além disso, alguns parâmetros foram definidos a partir do diagnóstico da realidade educacional, tendo em vista não propor um PQR que seja inacessível ou inferior a situação já existente<sup>(1)</sup>.
    </p>

    <p class="text">
      Cabe ressaltar que os parâmetros apresentados no SimCAQ são apenas um esforço inicial de definição desses parâmetros necessários para que o simulador elabore cenários para o financiamento da educação básica em nível nacional ou nos estados e municípios. Desse modo, não são, portanto, uma proposição definitiva, até mesmo porque compartilha-se do entendimento de que oferta de ensino em condições de qualidade decorre de fatores históricos e sociais que necessitam de definição no âmbito de um processo democrático por meio das instituições responsáveis para esta tarefa em diálogo com a sociedade.
    </p>

    <p class="text">
      Os parâmetros que se referem mais especificamente às condições de qualidade são pensados tendo como base as especificidades das etapas e modalidades<sup>(2)</sup>, bem como a área de localização (urbana ou rural) e o tamanho das escolas.
    </p>
  </div>

  <hr />

  <h3 class="section-title">
    CONHEÇA O PADRÃO DE QUALIDADE DE REFERÊNCIA (PQR) PARA O CÁLCULO DO CAQ
  </h3>

  <p>
    A proposta de valores do PQR apresentada nesse documento foi realizada pela
    equipe do projeto. Desse modo, não foram decisões do governo federal, dos
    governos estaduais ou municipais. Por consequência, os resultados do SimCAQ
    decorrentes dos parâmetros de qualidade propostos não vinculam a
    responsabilidade de repasses de recursos financeiros por parte dos entes
    federativos.
  </p>

  <hr />

  <div class="buttons-container">
    <button *ngIf="!todosOsPaineisEstaoAbertos; else botaoAlternativo" mat-flat-button class="aux-button"
      (click)="openAllPanels()">
      <mat-icon fontIcon class="menu-icon"> add_box </mat-icon>
      Expandir parâmetros
    </button>
    <ng-template #botaoAlternativo>
      <button mat-flat-button class="aux-button" (click)="closeAllPanels()">
        <mat-icon fontIcon class="menu-icon">
          indeterminate_check_box
        </mat-icon>
        Recolher todos
      </button>
    </ng-template>

    <a href="../../../assets/SIMCAQ_PQR.pdf" download target="_blank">
      <button mat-flat-button class="aux-button">
        <mat-icon fontIcon class="menu-icon"> picture_as_pdf </mat-icon>
        Documento PQR atual e justificativas
      </button>
    </a>

    <a href="../../../assets/SIMCAQ_ANOS_ANTERIORES.zip" download target="_blank">
      <button mat-flat-button class="aux-button" >
        <mat-icon fontIcon class="menu-icon"> picture_as_pdf </mat-icon>
        Documento PQR anos anteriores
      </button>
    </a>

    <button *ngIf="isLogado && !ehEdicao; else botaoCancelarEdicao" mat-flat-button class="aux-button" (click)="habiliteEdicao()">
      <mat-icon fontIcon class="menu-icon"> edit </mat-icon>
      Habilitar edição
    </button>

    <ng-template #botaoCancelarEdicao>
      <button
        *ngIf="isLogado"
        mat-flat-button class="aux-button" (click)="desabiliteEdicao()">
        <mat-icon fontIcon class="menu-icon">
          close
        </mat-icon>
        Desabilitar edição
      </button>
    </ng-template>

    <button
      *ngIf="isLogado"
      mat-flat-button class="aux-button" (click)="solicitarRecalculoMemoriaCalculo()">
      <mat-icon fontIcon class="menu-icon"> calculate </mat-icon>
      Recalcular Memória de Cálculo
    </button>
  </div>

  <hr />

  <div class="accordions-container">
    <app-painel-expansao #painelDiasLetivos titulo="Número de dias letivos">
      <div class="accordion-body">
        <div class="panel">
          <app-tabela [cabecalhoCustomizado]="true" ngSkipHydration class="table-component">
            <ng-container cabecalho>
              <tr>
                <th class="table-header">
                  Etapa
                </th>
                <th class="table-header">
                  Número de dias letivos
                </th>
              </tr>
            </ng-container>
            <ng-container corpo>
              <tr *ngFor="let item of pqrNumeroDiasLetivos" class="body">
                <td>
                  {{ item.etapaEnsino.descricao }}
                </td>
                <td>
                  <input [disabled]="!ehEdicao"
                    [ngClass]="ehEdicao ? 'display-input text-green w-100 input-em-edicao' : 'display-input text-green w-100'"
                    [(ngModel)]="item.qntDiasLetivos" appOnlyNumbers />
                </td>
              </tr>
            </ng-container>
          </app-tabela>
        </div>

        <button *ngIf="ehEdicao" mat-flat-button class="aux-button" (click)="salvePQRNumeroDiasLetivos()">
          <mat-icon fontIcon class="menu-icon">check </mat-icon>
          Salvar
        </button>
      </div>
    </app-painel-expansao>

    <app-painel-expansao titulo="Carga horária de ensino" #painelCargaHoraria>
      <div class="accordion-body">
        <div class="panel">
          <app-tabela [cabecalhos]="[
              { titulo: 'Etapa', tamanho: '60%' },
              { titulo: 'Turno', tamanho: '20%' },
              {
                titulo: 'Carga horária diária',
                subtitulos: ['PQR'],
                tamanho: '20%'
              }
            ]" ngSkipHydration class="table-component">
            <ng-container corpo>
              <ng-container *ngFor="let dados of pqrCargaHoraria; index as i">
                <tr class="body" *ngIf="dados.etapaEnsino.descricao !== 'Ensino Médio'">
                  <td rowspan="2" *ngIf="i < 9 && i % 2 === 0">
                    {{ obterDescricaoEtapaCargaHoraria(dados.etapaEnsino.descricao) }}
                  </td>

                  <td rowspan="2" *ngIf="i > 9 && i % 2 === 1">
                    {{ obterDescricaoEtapaCargaHoraria(dados.etapaEnsino.descricao) }}
                  </td>

                  <td class="align-left">
                    {{ dados.tipoTurno.descricao }}
                  </td>

                  <td>
                    <input [disabled]="!ehEdicao"
                      [ngClass]="ehEdicao ? 'display-input text-green w-100 input-em-edicao' : 'display-input text-green w-100'"
                      [(ngModel)]="dados.qntCargaHorariaDiaria" appOnlyNumbers />
                  </td>
                </tr>

                <tr class="body" *ngIf="dados.etapaEnsino.descricao === 'Ensino Médio'">
                  <td *ngIf="i === 8" rowspan="3">
                    {{ obterDescricaoEtapaCargaHoraria(dados.etapaEnsino.descricao) }}
                  </td>

                  <td class="align-left">
                    {{ dados.tipoTurno.descricao }}
                  </td>

                  <td>
                    <input [disabled]="!ehEdicao"
                      [ngClass]="ehEdicao ? 'display-input text-green w-100 input-em-edicao' : 'display-input text-green w-100'"
                      [(ngModel)]="dados.qntCargaHorariaDiaria" appOnlyNumbers />
                  </td>
                </tr>
              </ng-container>
            </ng-container>
          </app-tabela>
        </div>

        <button *ngIf="ehEdicao" mat-flat-button class="aux-button" (click)="salvePQRCargaHorariaEnsino()">
          <mat-icon fontIcon class="menu-icon">edit </mat-icon>
          Salvar
        </button>
      </div>
    </app-painel-expansao>

    <app-painel-expansao titulo="Tamanho das turmas" #painelTamanhoTurmas>
      <div class="accordion-body">
        <div class="panel panel-tamanho-turma" *ngIf="featureFlagService.funcionalidadeHabilitada('1108-pqr-minimo-turma') && featureFlagService.funcionalidadeHabilitada('1104-creche-idade'); else pqrTamanhoTurmasAntigo">
          <app-tabela [cabecalhoCustomizado]="true" class="table-component" ngSkipHydration>
            <ng-container cabecalho>
              <tr>
                <th class="table-header" rowspan="3" colspan="6" style="width: 34%">
                  Etapa / Segmento / Modalidade
                </th>
                <th class="table-header" rowspan="1" colspan="4" style="width: 22%; text-align: center !important;">
                  Escolas URBANAS
                </th>
                <th class="table-header" rowspan="1" colspan="4" style="width: 22%">
                  Escolas de áreas RURAIS
                </th>
                <th class="table-header" rowspan="2" colspan="4" style="width: 22%">
                  Critério para criação de turmas
                </th>
              </tr>
              <tr>
                <th class="table-header" rowspan="2" colspan="2" style="width: 15%">
                  Alunos por turma (Número Máximo)
                </th>
                <th class="table-header" rowspan="2" colspan="2" style="width: 11%">
                  Docentes por turma
                </th>
                <th class="table-header" rowspan="2" colspan="2" style="width: 15%">
                  Alunos por turma (Número Máximo)
                </th>
                <th class="table-header" rowspan="2" colspan="2" style="width: 11%">
                  Docentes por turma
                </th>
              </tr>
              <tr>
                <th class="table-header" rowspan="1" colspan="2">Número mínimo de turmas</th>
                <th class="table-header" rowspan="1" colspan="2">Número mínimo de matrículas para abrir o número mínimo de turmas</th>
              </tr>
            </ng-container>
            <ng-container corpo>
              <ng-container *ngFor="let dados of pqrTamanhoTurmasFiltradas; index as i">
                <tr *ngIf="i % 2 === 0" class="body">
                  <td class="table-cell align-left" colspan="6">
                    {{ dados.etapaEnsino.descricao }}
                  </td>
                  <td class="table-cell align-center" colspan="2">
                    <input [disabled]="!ehEdicao"
                      [ngClass]="ehEdicao ? 'display-input text-green input-em-edicao' : 'display-input text-green'"
                      [(ngModel)]="dados.qntAlunosTurma" appOnlyNumbers
                      style="display: inherit !important;" />
                  </td>
                  <td class="table-cell align-center" colspan="2">
                    <input [disabled]="!ehEdicao"
                      [ngClass]="ehEdicao ? 'display-input text-green input-em-edicao' : 'display-input text-green'"
                      [(ngModel)]="dados.qntDocentesTurma" appOnlyNumbers
                      style="display: inherit !important;" />
                  </td>
                  <td class="table-cell align-center" colspan="2">
                    <input [disabled]="!ehEdicao"
                      [ngClass]="ehEdicao ? 'display-input text-green input-em-edicao' : 'display-input text-green'"
                      [(ngModel)]="pqrTamanhoTurmasFiltradas[i + 1].qntAlunosTurma" appOnlyNumbers
                      style="display: inherit !important;" />
                  </td>
                  <td class="table-cell align-center" colspan="2">
                    <input [disabled]="!ehEdicao"
                      [ngClass]="ehEdicao ? 'display-input text-green input-em-edicao' : 'display-input text-green'"
                      [(ngModel)]="pqrTamanhoTurmasFiltradas[i + 1].qntDocentesTurma" appOnlyNumbers
                      style="display: inherit !important;" />
                  </td>
                  <td class="table-cell align-center" colspan="2">
                    <input *ngIf="pqrTamanhoMinimoTurmas[i / 2]" [disabled]="!ehEdicao"
                      [ngClass]="ehEdicao ? 'display-input text-green input-em-edicao' : 'display-input text-green'"
                      [(ngModel)]="pqrTamanhoMinimoTurmas[i / 2].qntMinimaTurma" appOnlyNumbers
                      style="display: inherit !important;" />
                  </td>
                  <td class="table-cell align-center" colspan="2">
                    <input *ngIf="pqrTamanhoMinimoTurmas[i / 2]" [disabled]="!ehEdicao"
                      [ngClass]="ehEdicao ? 'display-input text-green input-em-edicao' : 'display-input text-green'"
                      [(ngModel)]="pqrTamanhoMinimoTurmas[i / 2].qntMinimaAlunosTurma" appOnlyNumbers
                      style="display: inherit !important;" />
                  </td>
                </tr>
              </ng-container>
            </ng-container>
          </app-tabela>
        </div>

        <ng-template #pqrTamanhoTurmasAntigo>
          <div class="panel">
            <app-tabela [cabecalhoCustomizado]="true" class="table-component" ngSkipHydration>
              <ng-container cabecalho>
                <tr>
                  <th class="table-header" rowspan="2">Etapa / Modalidade</th>
                  <th class="table-header" colspan="2">Escolas URBANAS</th>
                  <th class="table-header" colspan="2">Escolas de áreas RURAIS</th>
                </tr>
                <tr>
                  <th class="table-header header-nowrap">Alunos por turma (Número Máximo)</th>
                  <th class="table-header">Docentes por turma</th>
                  <th class="table-header header-nowrap">Alunos por turma (Número Máximo)</th>
                  <th class="table-header">Docentes por turma</th>
                </tr>
              </ng-container>
              <ng-container corpo>
                <ng-container *ngFor="let dados of pqrTamanhoTurmasFiltradas; index as i">
                  <tr *ngIf="i % 2 === 0" class="body">
                    <td>
                      {{ dados.etapaEnsino.descricao }}
                    </td>
                    <td>
                      <input [disabled]="!ehEdicao"
                        [ngClass]="ehEdicao ? 'display-input text-green input-em-edicao' : 'display-input text-green'"
                        [(ngModel)]="dados.qntAlunosTurma" appOnlyNumbers />
                    </td>
                    <td>
                      <input [disabled]="!ehEdicao"
                        [ngClass]="ehEdicao ? 'display-input text-green input-em-edicao' : 'display-input text-green'"
                        [(ngModel)]="dados.qntDocentesTurma" appOnlyNumbers />
                    </td>
                    <td>
                      <input [disabled]="!ehEdicao"
                        [ngClass]="ehEdicao ? 'display-input text-green input-em-edicao' : 'display-input text-green'"
                        [(ngModel)]="pqrTamanhoTurmasFiltradas[i + 1].qntAlunosTurma" appOnlyNumbers />
                    </td>
                    <td>
                      <input [disabled]="!ehEdicao"
                        [ngClass]="ehEdicao ? 'display-input text-green input-em-edicao' : 'display-input text-green'"
                        [(ngModel)]="pqrTamanhoTurmasFiltradas[i + 1].qntDocentesTurma" appOnlyNumbers />
                    </td>
                  </tr>
                </ng-container>
              </ng-container>
            </app-tabela>
          </div>
        </ng-template>

        <button *ngIf="ehEdicao" mat-flat-button class="aux-button" (click)="salvePQRTamanhoTurma()">
          <mat-icon fontIcon class="menu-icon">edit </mat-icon>
          Salvar
        </button>
      </div>
    </app-painel-expansao>

    <app-painel-expansao titulo="Docentes: formação e remuneração" #painelCarreiraProfessores>
      <div class="accordion-body">
        <div class="panel">
          <app-tabela [cabecalhos]="[
              {
                titulo: 'Nível / Classe',
                subtitulos: ['Seq.', 'Denominação'],
                tamanho: '15%'
              },
              { titulo: 'Nível de formação ', tamanho: '15%' },
              {
                titulo: 'Jornada',
                subtitulos: [
                  'Jornada total (em horas)',
                  'Sem interação (%)',
                  'Com interação (%)'
                ],
                tamanho: '40%'
              },
              {
                titulo: 'Remuneração bruta mensal',
                subtitulos: ['PQR (R$)'],
                tamanho: '15%'
              },
              { titulo: 'Mês/ano referência', tamanho: '15%' }
            ]" class="table-component" ngSkipHydration>
            <ng-container corpo>
              <tr *ngFor="let dados of pqrCarreiraProfessores" class="body">
                <td class="align-center">
                  <strong>{{ dados.denominacao }}</strong>
                </td>
                <td>
                  <input [disabled]="!ehEdicao"
                    [ngClass]="ehEdicao ? 'display-input align-left input-em-edicao' : 'display-input align-left'"
                    [(ngModel)]="dados.denominacao" appOnlyNumbers />
                </td>
                <td>
                  <select [disabled]="!ehEdicao"
                    [ngClass]="ehEdicao ? 'display-input w-100 align-left input-em-edicao' : 'display-input w-100 align-left'"
                    [(ngModel)]="dados.tipoNivelFormacao.codTipoNivelFormacao">
                    <option [selected]="dados.tipoNivelFormacao.codTipoNivelFormacao == formacao.codTipoNivelFormacao"
                      *ngFor="let formacao of opcoesFormacao" [ngValue]="formacao.codTipoNivelFormacao">
                      {{ formacao.descricao }}
                    </option>

                  </select>
                </td>
                <td>
                  <input [disabled]="!ehEdicao"
                    [ngClass]="ehEdicao ? 'display-input w-100 align-center text-green input-em-edicao' : 'display-input w-100 align-center text-green'"
                    [(ngModel)]="dados.jornadaTotal" appOnlyNumbers />
                </td>
                <td>
                  <input [disabled]="!ehEdicao" [maskito]="mask_porcentagem_limpa"
                    [ngClass]="ehEdicao ? 'display-input w-100 text-green input-em-edicao' : 'display-input w-100 text-green'"
                    [(ngModel)]="dados.jornadaSemInteracao" />
                </td>
                <td>
                  <input [disabled]="!ehEdicao" [maskito]="mask_porcentagem_limpa"
                    [ngClass]="ehEdicao ? 'display-input text-green w-100 input-em-edicao' : 'display-input text-green w-100'"
                    [(ngModel)]="dados.jornadaComInteracao" />
                </td>
                <td>
                  <input [disabled]="!ehEdicao" appPtBrNumber
                    [ngClass]="ehEdicao ? 'display-input text-green w-100 input-em-edicao' : 'display-input text-green w-100'"
                    [(ngModel)]="dados.remuneracaoBrutaMensal" />
                </td>
                <td>
                  <input [disabled]="!ehEdicao" [maskito]="mask_mes_ano" [id]="dados.codPqrFormacaoJornadaRemuneracaoProfessores"
                    [ngClass]="ehEdicao ? 'display-input w-100 align-center input-em-edicao' : 'display-input w-100 align-center'"
                    [value]="transformarValorParaData(dados.mesAnoReferencia)"
                    (input)="atualizeDataPQRCarreiraProfessor($event)" />
                </td>
              </tr>
            </ng-container>
          </app-tabela>
        </div>



        <button *ngIf="ehEdicao" mat-flat-button class="aux-button" (click)="salvePQRCarreiraRemuneracaoProfessores()">
          <mat-icon fontIcon class="menu-icon">edit </mat-icon>
          Salvar
        </button>
      </div>
    </app-painel-expansao>

    <app-painel-expansao titulo="Funcionários: quadro e remuneração" #painelQuadroFuncionarios>
      <div class="accordion-body">
        <div class="panel">
          <app-tabela [cabecalhoCustomizado]="true" class="table-component" ngSkipHydration>
            <ng-container cabecalho>
              <tr>
                <th class="table-header" rowspan="2" colspan="2">Função</th>
                <th class="table-header" rowspan="2" colspan="3">
                  Critérios de alocação (PQR)
                </th>
                <th class="table-header" rowspan="3" style="width: 12%">
                  Nível de formação
                </th>
                <th class="table-header" rowspan="3" style="width: 12%">
                  Remuneração bruta mensal para uma jornada semanal de 40h
                </th>
                <th class="table-header" rowspan="3" style="width: 10%">
                  Mês/ano referência
                </th>
              </tr>

              <tr></tr>
              <tr>
                <th class="table-header" style="width: 2%">Cód. Função</th>
                <th class="table-header" style="width: 26%">Área de atuação</th>
                <th class="table-header" style="width: 6%">
                  Quantidade de matrícula / Profissional
                </th>
                <th class="table-header" style="width: 2%">Mínimo de matrícula / Função</th>
                <th class="table-header" style="width: 2%">Máximo / Escola</th>
              </tr>
            </ng-container>
            <ng-container corpo>
              <tr *ngFor="let dados of pqrQuadroFuncionarios" class="body">
                <td class="align-center">{{ dados.codPqrQuadroFuncionariosRemuneracao }}</td>
                <td>
                  <input [disabled]="!ehEdicao"
                    [ngClass]="ehEdicao ? 'display-input w-100 align-left input-em-edicao' : 'display-input w-100 align-left'"
                    [(ngModel)]="dados.tipoFuncao.areaAtuacao" appOnlyNumbers />
                </td>
                <td>
                  <input [disabled]="!ehEdicao"
                    [ngClass]="ehEdicao ? 'display-input w-100 text-green input-em-edicao' : 'display-input w-100 text-green'"
                    [(ngModel)]="dados.qntMatriculaProfissional" appOnlyNumbers />

                </td>
                <td>
                  <input [disabled]="!ehEdicao"
                    [ngClass]="ehEdicao ? 'display-input w-100 text-green input-em-edicao' : 'display-input w-100 text-green'"
                    [(ngModel)]="dados.qntMinMatriculaFuncao" appOnlyNumbers />
                </td>
                <td>
                  <input [disabled]="!ehEdicao"
                    [ngClass]="ehEdicao ? 'display-input w-100 text-green input-em-edicao' : 'display-input w-100 text-green'"
                    [(ngModel)]="dados.qntMaxEscola" appOnlyNumbers />
                </td>
                <td>
                  <select [disabled]="!ehEdicao"
                    [ngClass]="ehEdicao ? 'display-input w-100 align-left input-em-edicao' : 'display-input w-100 align-left'"
                    [(ngModel)]="dados.tipoNivelFormacao.codTipoNivelFormacao">
                    <option [selected]="dados.tipoNivelFormacao.codTipoNivelFormacao == formacao.codTipoNivelFormacao"
                      *ngFor="let formacao of opcoesFormacao" [ngValue]="formacao.codTipoNivelFormacao">
                      {{ formacao.descricao }}
                    </option>

                  </select>
                </td>
                <td>
                  <input [disabled]="!ehEdicao" appPtBrNumber
                    [ngClass]="ehEdicao ? 'display-input w-100 text-green input-em-edicao' : 'display-input w-100 text-green'"
                    [(ngModel)]="dados.remuneracaoBrutaMensal" />
                </td>
                <td>
                  <input [disabled]="!ehEdicao" [maskito]="mask_mes_ano" [id]="dados.codPqrQuadroFuncionariosRemuneracao"
                         [ngClass]="ehEdicao ? 'display-input w-100 align-center input-em-edicao' : 'display-input w-100 align-center'"
                         [value]="transformarValorParaData(dados.mesAnoReferencia)"
                         (input)="atualizeDataPQRQuadroFuncionario($event)" />
                </td>
              </tr>
            </ng-container>
          </app-tabela>
        </div>



        <button *ngIf="ehEdicao" mat-flat-button class="aux-button" (click)="salvePQRQuadroRemuneracao()">
          <mat-icon fontIcon class="menu-icon">edit </mat-icon>
          Salvar
        </button>
      </div>
    </app-painel-expansao>

    <app-painel-expansao [titulo]="'Adicional de remuneração'" #painelAdicionalRemuneracao>
      <div class="accordion-body">
        <div class="panel">
          <app-tabela [cabecalhos]="[
              { titulo: 'Item', tamanho: '80%' },
              { titulo: 'Percentual (%)', tamanho: '20%' }
            ]" class="table-component" ngSkipHydration>
            <ng-container corpo>
              <tr *ngFor="let dados of pqrAdicionais  " class="body">
                <td>
                  {{ obterDescricaoItemAdicional(dados) }}
                </td>
                <td>
                  <input [attr.disabled]="ehEdicao ? null : ''" [maskito]="mask_porcentagem_limpa"
                         [ngClass]="ehEdicao ? 'display-input w-100 text-green input-em-edicao' : 'display-input w-100 text-green'"
                         [(ngModel)]="dados.percentual" />
                </td>
              </tr>
            </ng-container>
          </app-tabela>
        </div>

      </div>

      <button *ngIf="ehEdicao" mat-flat-button class="aux-button" (click)="salvePQRAdicionais()">
        <mat-icon fontIcon class="menu-icon">edit </mat-icon>
        Salvar
      </button>
    </app-painel-expansao>

    <app-painel-expansao titulo="Serviços, infraestrutura e recursos" #painelServicosInfra>
      <div class="accordion-body">
        <div class="panel">
          <app-tabela
            ngSkipHydration
            class="table-component"
            [cabecalhoCustomizado]="true"
          >
            <ng-container cabecalho>
              <tr>
                <th class="table-header" style="width: 4%">
                  <div class="button-wrapper">
                    <button
                      mat-icon-button
                      class="add-button"
                      (click)="adicionarDespesa()"
                      [disabled]="!ehEdicao"
                    >
                      <mat-icon>add</mat-icon>
                    </button>
                  </div>
                </th>
                <th class="table-header align-center" style="width: 8%">
                  Seq.
                </th>
                <th class="table-header" style="width: 68%">
                  Item
                </th>
                <th class="table-header" style="width: 20%">
                  Percentual (%)
                </th>
              </tr>
            </ng-container>
            <ng-container corpo>
              <tr *ngFor="let dados of pqrManutencaoEscolas; index as i">
                <td class="table-cell">
                  <div class="button-wrapper">
                    <button
                      mat-icon-button
                      class="delete-button"
                      (click)="removerDespesa(i)"
                      [disabled]="!ehEdicao"
                    >
                      <mat-icon>close</mat-icon>
                    </button>
                  </div>
                </td>
                <td class="align-center">{{ dados.id }}</td>
                <td class="table-cell">
                  <input
                    class="display-input align-left"
                    type="text"
                    [disabled]="!ehEdicao"
                    [ngClass]="ehEdicao ? 'display-input w-100 align-left input-em-edicao' : 'display-input w-100 align-left'"
                    [(ngModel)]="dados.descricaoItem"
                    (ngModelChange)="calcularTotalPercentualManutencaoEscolas()"
                  />
                </td>
                <td class="table-cell">
                  <input
                    class="display-input align-right text-green"
                    type="text"
                    [disabled]="!ehEdicao"
                    [maskito]="mask_porcentagem_limpa"
                    [ngClass]="ehEdicao ? 'display-input w-100 text-green input-em-edicao' : 'display-input w-100 text-green'"
                    [(ngModel)]="dados.percentual"
                    (ngModelChange)="calcularTotalPercentualManutencaoEscolas()"
                  />
                </td>
              </tr>
            </ng-container>
            <ng-container rodape>
              <tr>
                <td class="table-cell bg-gray"></td>
                <td class="table-cell bg-gray"></td>
                <td class="table-cell bg-gray align-left bold">
                  <span>Total</span>
                </td>
                <td>
                  <input disabled [value]="totalPercentualManutencaoEscolas"
                         class="display-input w-100 text-green" />
                </td>
              </tr>
            </ng-container>
          </app-tabela>
        </div>

        <button *ngIf="ehEdicao" mat-flat-button class="aux-button" (click)="salvePQRManutencaoEscolas()">
          <mat-icon fontIcon class="menu-icon">edit </mat-icon>
          Salvar
        </button>
      </div>
    </app-painel-expansao>
    <app-painel-expansao titulo="Alimentação escolar" [habilitado]="true" #painelAlimentacaoEscolar>
      <div class="accordion-body">
        <div class="panel">
          <app-tabela [cabecalhos]="[
              { titulo: 'Etapa/segmento/modalidade', tamanho: '40%' },
              { titulo: 'Número de dias letivos', tamanho: '10%' },
              { titulo: 'Per Capita (R$)', subtitulos: ['Parcial', 'Integral'], tamanho: '25%' },
              { titulo: 'Per Capita (R$) (Indígenas e Quilombolas)', subtitulos: ['Parcial', 'Integral'], tamanho: '25%' },
            ]" class="table-component" ngSkipHydration>
            <ng-container corpo>
              <ng-container *ngFor="let dados of pqrAlimentacaoEscolar; index as i" >
                <tr *ngIf="i % 2 === 0"  class="body">
                  <td>
                    {{ dados.etapaEnsino.descricao }}
                  </td>
                  <td>
                    <input [disabled]="!ehEdicao"
                           [ngClass]="ehEdicao ? 'display-input w-100 text-green input-em-edicao' : 'display-input w-100 text-green'"
                           [(ngModel)]="dados.numeroDiasLetivos" appOnlyNumbers />
                  </td>
                  <td class="align-center">
                    <input [disabled]="!ehEdicao"
                           appPtBrNumber
                           [ngClass]="ehEdicao ? 'display-input w-100 text-green input-em-edicao' : 'display-input w-100 text-green'"
                           [(ngModel)]="dados.valorRefeicao" />
                  </td>
                  <td class="align-center">
                    <input [disabled]="!ehEdicao"
                           *ngIf="i !== 10"
                           appPtBrNumber
                           [ngClass]="ehEdicao ? 'display-input w-100 text-green input-em-edicao' : 'display-input w-100 text-green'"
                           [(ngModel)]="pqrAlimentacaoEscolar[i + 1].valorRefeicao"/>
                  </td>
                  <td class="align-center">
                    <input [disabled]="!ehEdicao"
                           appPtBrNumber
                           [ngClass]="ehEdicao ? 'display-input w-100 text-green input-em-edicao' : 'display-input w-100 text-green'"
                           [(ngModel)]="dados.valorRefeicaoIndigenaQuilombola"/>
                  </td>
                  <td class="align-center" >
                    <input [disabled]="!ehEdicao"
                           *ngIf="i !== 10"
                           appPtBrNumber
                           [ngClass]="ehEdicao ? 'display-input w-100 text-green input-em-edicao' : 'display-input w-100 text-green'"
                           [(ngModel)]="pqrAlimentacaoEscolar[i + 1].valorRefeicaoIndigenaQuilombola"/>
                  </td>
                </tr>
              </ng-container>
            </ng-container>
          </app-tabela>
        </div>



        <button *ngIf="ehEdicao" mat-flat-button class="aux-button" (click)="salvePQRAlimentacaoEscolar()">
          <mat-icon fontIcon class="menu-icon">edit </mat-icon>
          Salvar
        </button>
      </div>
    </app-painel-expansao>

    <app-painel-expansao titulo="Adicional para modalidades e contextos" #painelFatorPonderacao>
      <div class="accordion-body">
        <div class="panel">
          <app-tabela [cabecalhos]="[
              { titulo: 'Item', tamanho: '80%' },
              { titulo: 'Fator de Ponderação', tamanho: '20%' }
            ]" class="table-component" ngSkipHydration>
            <ng-container corpo>
              <tr *ngIf="pqrFatorPonderacao" class="body">
                <td>Educação Escolar Indígena</td>
                <td>
                  <input [attr.disabled]="ehEdicao ? null : ''" [maskito]="mask_fator_ponderacao"
                         [ngClass]="ehEdicao ? 'display-input w-100 text-green input-em-edicao' : 'display-input w-100 text-green'"
                         [(ngModel)]="pqrFatorPonderacao.pqrFatorPondIndigena"
                         (blur)="garantirMinimo('pqrFatorPondIndigena')"/>
                </td>
              </tr>
              <tr *ngIf="pqrFatorPonderacao" class="body">
                <td>Educação Escolar Quilombola</td>
                <td>
                  <input [attr.disabled]="ehEdicao ? null : ''" [maskito]="mask_fator_ponderacao"
                         [ngClass]="ehEdicao ? 'display-input w-100 text-green input-em-edicao' : 'display-input w-100 text-green'"
                         [(ngModel)]="pqrFatorPonderacao.pqrFatorPondQuilombola"
                         (blur)="garantirMinimo('pqrFatorPondQuilombola')"/>
                </td>
              </tr>
              <tr *ngIf="pqrFatorPonderacao" class="body">
                <td>Educação Profissional Integrada e concomitante ao Ensino Médio<sup>(1)</sup></td>
                <td>
                  <input [attr.disabled]="ehEdicao ? null : ''" [maskito]="mask_fator_ponderacao"
                         [ngClass]="ehEdicao ? 'display-input w-100 text-green input-em-edicao' : 'display-input w-100 text-green'"
                         [(ngModel)]="pqrFatorPonderacao.pqrFatorPondEnsMedioProfissional"
                         (blur)="garantirMinimo('pqrFatorPondEnsMedioProfissional')"/>
                </td>
              </tr>
              <tr *ngIf="pqrFatorPonderacao" class="body">
                <td>Educação Profissional Integrada e concomitante à EJA<sup>(2)</sup></td>
                <td>
                  <input [attr.disabled]="ehEdicao ? null : ''" [maskito]="mask_fator_ponderacao"
                         [ngClass]="ehEdicao ? 'display-input w-100 text-green input-em-edicao' : 'display-input w-100 text-green'"
                         [(ngModel)]="pqrFatorPonderacao.pqrFatorPondEjaProfissional"
                         (blur)="garantirMinimo('pqrFatorPondEjaProfissional')"/>
                </td>
              </tr>
              <tr *ngIf="pqrFatorPonderacao" class="body">
                <td>Atendimento Educacional Especializado (AEE)<sup>(3)</sup></td>
                <td>
                  <input [attr.disabled]="ehEdicao ? null : ''" [maskito]="mask_fator_ponderacao"
                         [ngClass]="ehEdicao ? 'display-input w-100 text-green input-em-edicao' : 'display-input w-100 text-green'"
                         [(ngModel)]="pqrFatorPonderacao.pqrFatorPondAee"
                         (blur)="garantirMinimo('pqrFatorPondAee')"/>
                </td>
              </tr>
            </ng-container>

          </app-tabela>
        </div>
      </div>
      <p class="description-note">
          <sup>(1)</sup> Considera as matrículas da Educação Profissional integradas e concomitantes ao Ensino Médio do Censo Escolar.
      </p>
      <p class="description-note">
        <sup>(2)</sup> Considera as matrículas da Educação Profissional integradas e concomitantes à EJA identificadas no Censo Escolar.
      </p>
      <p class="description-note">
        <sup>(3)</sup> Considera as matrículas do Público de Educação Especial (PEE) identificadas no Censo Escolar.
      </p>
      <button *ngIf="ehEdicao" mat-flat-button class="aux-button" (click)="salvePQRFatorPonderacao()">
       <mat-icon fontIcon class="menu-icon">edit</mat-icon>
       Salvar
     </button>
    </app-painel-expansao>

    <app-painel-expansao titulo="Direitos e obrigações trabalhistas" #painelDireitosTrabalhistas>
      <div class="accordion-body">
        <div class="panel">
          <app-tabela [cabecalhoCustomizado]="true" ngSkipHydration class="table-component">
            <ng-container cabecalho>
              <tr>
                <th class="table-header">Item</th>
                <th class="table-header" style="width: 20%">Percentual (%)</th>
              </tr>
            </ng-container>
            <ng-container corpo>
              <tr *ngFor="let dados of pqrEncargosSociais" class="body">
                <td class="align-left">
                  Direitos e obrigações trabalhistas (despesa com previdência parte patronal)
                </td>
                <td>
                  <input [disabled]="!ehEdicao" [maskito]="mask_porcentagem_limpa"
                    [ngClass]="ehEdicao ? 'display-input w-100 text-green input-em-edicao' : 'display-input w-100 text-green'"
                    [(ngModel)]="dados.percentual" />
                </td>
              </tr>
              <ng-container *ngIf="featureFlagService.funcionalidadeHabilitada('1184-pqr-multiplicador-anual')">
                <tr *ngFor="let dados of pqrMultiplicadorAnual" class="body">
                  <td class="align-left">
                    Multiplicador para o cálculo da despesa anual com pessoal <sup>(1)</sup>
                  </td>
                  <td>
                    <input [disabled]="!ehEdicao" [maskito]="mask_porcentagem_limpa"
                      [ngClass]="ehEdicao ? 'display-input w-100 text-green input-em-edicao' : 'display-input w-100 text-green'"
                      [(ngModel)]="dados.multiplicadorAnual" />
                  </td>
                </tr>
              </ng-container>
              <tr *ngIf="!featureFlagService.funcionalidadeHabilitada('1184-pqr-multiplicador-anual')" class="body">
                <td class="align-left">
                  Multiplicador para o cálculo da despesa anual com pessoal <sup>(1)</sup>
                </td>
                <td>
                  <input disabled value="13,3"
                    class="display-input w-100 text-green" />
                </td>
              </tr>
            </ng-container>
          </app-tabela>
        </div>

        <p class="description-note">
          <sup>(1)</sup> O cálculo do multiplicador da despesa anual com pessoal considera o salário mensal (relativo a 12 meses), somado ao 13º salário e ao adicional de um terço constitucional de férias.
        </p>

        <button *ngIf="ehEdicao" mat-flat-button class="aux-button" (click)="salvePQREncargosSociais()">
          <mat-icon fontIcon class="menu-icon">edit </mat-icon>
          Salvar
        </button>
      </div>
    </app-painel-expansao>

  </div>

  <hr />

  <h3 class="section-title">
    Parâmetros da Infraestrutura escolar
  </h3>

  <div class="accordions-container">
    <app-painel-expansao titulo="Salas de aula: diagnóstico e projeção"
      [habilitado]="featureFlagService.funcionalidadeHabilitada('novas-salas')" #painelNovasConstrucoes>
      <div class="accordion-body">
        <div class="panel">
          <app-tabela [cabecalhos]="[
              { titulo: 'Demanda por construção', tamanho: '60%' },
              { titulo: 'Dimensão (m²)', tamanho: '20%' },
              { titulo: 'Unidade de medida', tamanho: '20%' }
            ]" class="table-component" ngSkipHydration>
            <ng-container corpo>
              <tr *ngFor="let dados of pqrNovasConstrucoes" class="body">
                <td>
                  {{ dados.descricaoTipoConstrucao }}
                </td>
                <td>
                  <input disabled [value]="dados.dimensao.toLocaleString('pt-BR')"
                    class="display-input w-100 text-green" />
                </td>
                <td class="align-center">
                  {{ dados.descricaoUnidadeMedida }}
                </td>
              </tr>
            </ng-container>
          </app-tabela>
        </div>


      </div>
    </app-painel-expansao>

    <app-painel-expansao titulo="Espaços escolares: reforma e adequação" [habilitado]="false" #painelInfraestruturaEscolar>
      <div class="accordion-body">
        <div class="panel">
          <app-tabela [cabecalhos]="[
              { titulo: 'Dependências escolares', tamanho: '50%' },
              {
                titulo: 'Parâmetros para adequação (PQR)',
                subtitulos: ['Dimensão m2', 'Unidade de Medida '],
                tamanho: '50%'
              }
            ]" class="table-component" ngSkipHydration>
            <ng-container corpo>
              <ng-container>
                <tr>
                  <td colspan="3" class="table-section-title align-left">
                    ESPAÇOS PARA ATIVIDADES PEDAGÓGICAS
                  </td>
                </tr>
                <tr *ngFor="let dados of pqrInfraestruturaEscolar; index as i" class="body">
                  <ng-container *ngIf="i < 9">
                    <td>
                      {{ dados.tipoDependenciaEscolar.descricao }}
                    </td>
                    <td>
                      <input disabled [value]="dados.dimensao.toLocaleString('pt-BR')"
                        class="display-input w-100 text-green" />
                    </td>
                    <td class="align-center">
                      {{ dados.descricaoUnidadeMedida }}
                    </td>
                  </ng-container>
                </tr>
                <tr>
                  <td colspan="3" class="table-section-title align-left">
                    ESPAÇOS ADMINISTRATIVOS E INFRAESTRUTURA BÁSICA
                  </td>
                </tr>
                <tr *ngFor="let dados of pqrInfraestruturaEscolar; index as i" class="body">
                  <ng-container *ngIf="i <= 9">
                    <td>
                      {{ dados.tipoDependenciaEscolar.descricao }}
                    </td>
                    <td>
                      <input disabled [value]="dados.dimensao.toLocaleString('pt-BR')"
                        class="display-input w-100 text-green" />
                    </td>
                    <td class="align-center">
                      {{ dados.descricaoUnidadeMedida }}
                    </td>
                  </ng-container>
                </tr>
              </ng-container>
            </ng-container>
          </app-tabela>
        </div>


      </div>
    </app-painel-expansao>
  </div>



  <hr />

  <h3 class="page-title">Notas</h3>

  <div class="notes-container">
    <p class="text">
      <sup>(1)</sup> Em virtude do novo formato de divulgação dos microdados do Censo
      Escolar, alguns dados de diagnóstico e, consequentemente, do estabelecimento do
      PQR precisaram ser alterados a partir da versão do SimCAQ 2024.
    </p>

    <p class="text"><sup>(2)</sup> Na versão atual do SimCAQ apresenta os cálculos
      de valores do CAQ para creche, pré-escola, ensino fundamental anos iniciais e
      finais (separadamente), ensino médio e educação de jovens e adultos (EJA) e AEE.
      Considerando às especificidades das diversas situações de oferta da educação
      profissional, educação bilíngue de surdos, escolas indígenas e quilombolas o
      SimCAQ pretende avançar no cálculo do CAQ destas modalidades em nova fase da
      pesquisa. Desta forma, cada matrícula da Educação Especial e Educação Profissional
      é financiada com o mesmo valor de CAQ corresponde às etapas regulares que os
      alunos frequentam.</p>
  </div>

  <h3 class="page-title">Referências</h3>

  <div class="references-container">
    <p class="text">
      ALVES. T. <strong>Desenvolvimento de um modelo de previsão de custos para planejamento de sistemas públicos de educação básica em condições de qualidade</strong>: uma aplicação a municípios de Goiás. 353f. Tese (Doutorado em Administração) - Faculdade de Economia, Administração e Contabilidade, Universidade de São Paulo, São Paulo, 2012.
    </p>

    <p class="text">
      ALVES, T.; SILVEIRA, A. D. ; SCHNEIDER, G. ; DEL FABRO, M. D. Financiamento da escola pública de educação básica: a proposta do Simulador de Custo-Aluno Qualidade. <strong>Educação &amp; Sociedade</strong>, v. 40, p. 1-40, 2019.
    </p>

    <p class="text">
      ALVES, T.; SILVEIRA, A. D. ; SCHNEIDER, G. Financiamento da educação básica: o grande desafio para os municípios. <strong>Retratos da Escola</strong>, v. 13, p. 391-413, 2019.
    </p>

    <p class="text">
      BRASIL Constituição (1988). <strong>Constituição da República Federativa do Brasil.</strong>Brasília, 5 out. 1988.
    </p>

    <p class="text">
      BRASIL. Instituto Nacional de Estudos e Pesquisas Educacionais Anísio Teixeira – Inep. <strong>Plano Nacional de Educação PNE 2014-2024</strong>: Linha de Base. Brasília, DF: Inep, 2015. 404 p.
    </p>

    <p class="text">
      BRASIL. <strong>Lei Complementar nº 220, de 31 de outubro de 2025</strong>. Institui o Sistema Nacional de Educação (SNE) e fixa normas para a cooperação entre a União, os Estados, o Distrito Federal e os Municípios para elaboração e implementação de políticas, de programas e de ações educacionais, em regime de colaboração. Disponível em: <a href="https://www.planalto.gov.br/ccivil_03/LEIS/LCP/Lcp220.htm" target="_blank">https://www.planalto.gov.br/ccivil_03/LEIS/LCP/Lcp220.htm</a>. Acesso em: 24 nov. 2025.
    </p>

    <p class="text">
      BRASIL. Lei nº 9.394, de 20 de dezembro de 1996. Estabelece diretrizes e bases da educação nacional. <strong>Diário Oficial da União</strong>, Brasília, DF, 23 dez. 1996. Disponível em: <a href="http://www.planalto.gov.br/ccivil_03/leis/l9394.htm" target="_blank">http://www.planalto.gov.br/ccivil_03/leis/l9394.htm</a>.
    </p>

    <p class="text">
      CAMPANHA NACIONAL PELO DIREITO À EDUCAÇÃO. <strong>CAQ e CAQi no PNE:</strong> quanto custa a educação pública de qualidade no Brasil. São Paulo, 2018.
    </p>
  </div>
</div>
